import Spinner from "components/Spinner";
import { Predicates } from "libraries/predicates/predicates";
import { useCallback, useEffect, useState } from "react";
import ItemNumberValuesImportTable from "routes/mpn/components/tcc-values-import/ItemNumberValuesImportTable";
import { useCreateMpnStore } from "../../context/create/CreateMpnContext";
import { CreateMpnActions } from "../../states/create";
import { CreateMpnActionTypes } from "../../states/create/data";
import useAxios from "../../../../utils/useAxios";
import GPCValuesImportTable from "../../components/tcc-values-import/GPCValuesImportTable";
import { formatedNonMoldedComponentId } from "../../helpers/mpnRequestDto";
import { uniqueCompositionItems } from "../../helpers/uniqueCompositionItems";
import { generateNodeId } from "./mpn-create.helper";

export type itemNumberValuesImportProps = {
  isCompositionLoading: boolean;
  isInApprovalProcess: boolean;
  isRevision: boolean;
};

export const ItemNumberValuesImport = ({
  isInApprovalProcess,
  isRevision,
  isCompositionLoading
}: itemNumberValuesImportProps) => {
  const { state, dispatch } = useCreateMpnStore();
  const axios = useAxios();

  //const compositions = state?.itemNumber?.tcc_compositions;
  const compositions = state?.itemNumber?.tcc_compositions?.map((comp: any) => ({
    ...comp,
    componentType: comp.componentType ?? comp.component_type,
    nonMold: comp.nonMold ?? comp.non_mold,
    pcs: comp.pcs ?? comp.nr_pcs,
    isMandatory: comp.isMandatory ?? comp.pch_commnd ?? "N",
    mktMaterial: comp.mktMaterial ?? "",
  }));


  // For now will never be restrictive.
  const isTccCompositionRestrictive = false;

  const [isLoadingProduct, setIsLoadingProduct] = useState(false);

  const getProductInformationData = async (id: any) => {
    const endpoint = `/product-information?id=${id}`;
    const endpoint2 = `/sku-composition/${id}`;

    try {
      // Product Information Tab Data
      setIsLoadingProduct(true);
      const [{ data: prodInfoDataRes }, { data: compListDataRes }] =
        await Promise.all([axios.get(endpoint), axios.get(endpoint2)]);

      const pp_id = prodInfoDataRes[0]?.pp_id ?? "";

      setIsLoadingProduct(false);

      // @ts-ignore
      let dataPromises: Promise<AxiosResponse>[] = [];
      if (pp_id) {
        const endpoint3 = `/primary-packing-materials?pp_id=${pp_id}`;
        dataPromises.push(axios.get(endpoint3));
      }

      let primaryPackingMaterials: any[] = [];

      dispatch(
        CreateMpnActions.setStateValue(CreateMpnActionTypes.SET_COMPOSITION, {
          ...compListDataRes,
          mpns: Array.from(compListDataRes?.skus).map((mpn: any) => ({
            ...mpn,
            node_id: generateNodeId(),
          })),
          skus: [],

          molded_components: compListDataRes?.molded_components?.map(
            (comp: any) => {
              const material = comp.material_description.split("-");
              const mold_description = comp.mold_description.split("-");
              const decoration_technique_description = comp.decoration_technique_description.includes("-") ?
                comp.decoration_technique_description.split("-") : comp.decoration_technique_description;
              const artwork_description = comp.artwork_description.split("-");

              return {
                ...comp,
                color_description: comp.color.name,
                color_family_name: comp.color?.color_family_name,
                material_description:
                  material.length > 1 ? material[1] : material,
                material_id:
                  material.length > 1 ? material[0].trim() : material,
                mold_description:
                  mold_description?.length > 1
                    ? mold_description[1]
                    : mold_description,
                decoration_technique_description:
                  decoration_technique_description?.length > 1
                    ? decoration_technique_description[1]
                    : decoration_technique_description,
                artwork_description:
                  artwork_description?.length > 1
                    ? artwork_description[1]
                    : artwork_description,
                artwork_id: `${comp.artwork_id}`.padStart(4, "0"),
                node_id: generateNodeId(),
              };
            },
          ),
          non_molded_components: formatedNonMoldedComponentId(
            compListDataRes?.non_molded_components,
          ),
        }),
      );

      dispatch(
        CreateMpnActions.setStateValue(CreateMpnActionTypes.SET_MPN_DATA, {
          ...state.mpnData,
          name:prodInfoDataRes[0]?.name,
          productId: prodInfoDataRes[0]?.id.replace(/^0+/, ""),
          description: prodInfoDataRes[0]?.description,
          parent_code: prodInfoDataRes[0]?.parent_code,
          primary_packing_materials: primaryPackingMaterials,
          pp_id: prodInfoDataRes[0]?.pp_id,
          product_width: prodInfoDataRes[0]?.width,
          product_length: prodInfoDataRes[0]?.length,
          product_height: prodInfoDataRes[0]?.height,
          stdpk: prodInfoDataRes[0]?.stdpk,
          stdpk_weight: prodInfoDataRes[0]?.stdpk_weight,
          stdpk_length: prodInfoDataRes[0]?.stdpk_length,
          stdpk_width: prodInfoDataRes[0]?.stdpk_width,
          stdpk_height: prodInfoDataRes[0]?.stdpk_height,
          stdpk_net_weight: prodInfoDataRes[0]?.stdpk_net_weight,
          product_net_weight: prodInfoDataRes[0]?.product_net_weight,
          product_gross_weight: prodInfoDataRes[0]?.product_gross_weight,
          giftbox:prodInfoDataRes[0]?.giftbox,
          product_use:prodInfoDataRes[0]?.product_use,
          reldt:prodInfoDataRes[0]?.reldt,
        }),
      );


      dispatch(
        CreateMpnActions.setStateValue(CreateMpnActionTypes.SET_CLONED_SKU_ID, state?.mpnToClone?.id),
      );

      // setAreOtherTabsLoading(false);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (state?.mpnToClone && !state.cloned_sku_id) {
      getProductInformationData(state?.mpnToClone?.id);
    }
  }, [state?.mpnToClone]);

  const gpcComposition = useCallback(
    (comp: any[]) => uniqueCompositionItems(comp),
    [],
  );


  return (
    <>
      {isCompositionLoading || isLoadingProduct ? (
        <Spinner />
      ) : (
        <>

          <div>
            <div className="card-header my-2 rounded d-flex">
              <h6 className="m-0 text-primary fw-bold">{`GPC Compositions Data Import`}</h6>
            </div>

            {isInApprovalProcess && (
              <p className="font-italic mb-1 fw-bold">
                As an approver this page is only informative.
              </p>
            )}

            {!isInApprovalProcess && isRevision && (
              <p className="font-italic mb-1 fw-bold">
                In a revision this page is only informative.
              </p>
            )}

            <p className="font-italic">
              {Predicates.isNullOrUndefined(state.global_product)
                ? "You didn't select any Global Product. Advance to MPN composition step"
                //: state.global_product?.gpc_composition
                : !state.global_product?.gpc_composition || state.global_product?.gpc_composition?.length === 0
                  ? "The selected Global Product doesn't have any data associated to import. Advance to MPN composition step."
                  : `Below is the pre-defined Global Product data. ${!isInApprovalProcess && !isRevision ? "You can import any amount of this data to the MPN composition or just proceed normally." : ""}`}
                  {/* : `Below is the pre-defined Global Product data for the ${state.global_product?.gpc_composition?.gpc_comtyp} region. ${!isInApprovalProcess && !isRevision ? "You can import any amount of this data to the MPN composition or just proceed normally." : ""}`} */}
            </p>

            {state.global_product?.gpc_composition?.length > 0 && (
                <GPCValuesImportTable
                  isInApprovalProcess={isInApprovalProcess || state.mpnRequest.status === "SUBMITTED"}
                  isRevision={isRevision}
                  composition={gpcComposition(state.global_product?.gpc_composition)}
                />
              )}
          </div>

          <div>
            <div className="card-header my-2 rounded d-flex">
              {/*<h6 className="m-0 text-primary fw-bold">{`Item Number Data Import ${compositions?.length > 0 ? `(${compositions?.name} - ${isTccCompositionRestrictive ? "Restrictive" : "Not Restrictive"})` : ""}`}</h6>*/}
              <h6 className="m-0 text-primary fw-bold">{`Item Number Data Import`}</h6>
            </div>

            <p className="font-italic">
              {Predicates.isNullOrUndefined(state.composition)
                ? "You didn't select any Item Number. Advance to MPN composition step"
                : !compositions || compositions.length === 0
                // : state.composition
                  ? "The selected Item Number doesn't have any data associated to import. Advance to MPN composition step."
                  // : `Below is the pre-defined Item Number data for the ${compositions?.name} region. ${!isInApprovalProcess && !isRevision ? "You can import any amount of this data to the MPN composition or just proceed normally." : ""}`}
                 : `Below is the pre-defined Item Number data. ${!isInApprovalProcess && !isRevision ? "You can import any amount of this data to the MPN composition or just proceed normally." : ""}`}
           
            </p>

            {/* {compositions?.length > 0 && !state.mpnToClone?.id && ( */}
            {compositions?.length > 0 && (  
              <ItemNumberValuesImportTable
                isInApprovalProcess={isInApprovalProcess}
                isRevision={isRevision || state.mpnRequest.status === "SUBMITTED"}
                composition={compositions}
              />
            )}
          </div>

          <p className="font-italic alert-warning"> WARNING: If you are <strong>Cloning a MPN</strong> and select anything here the cloned composition will be lost</p>

        </>
      )}
    </>
  );
};

